import {
  AsyncSelectOptions,
  type AsyncSelectProps,
} from "@/components/ui/FormFields/AsyncSelectOptions";
import {
  Badge,
  Button,
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  LoadingSpinner,
  Popover,
  PopoverContent,
  PopoverTrigger,
  Separator,
  Typography,
} from "@/components/ui";
import { SelectDropdownChevron } from "@/components/ui/shadcn/Select/Select";
import { TooltipWrapper } from "@/components/ui/shadcn/Tooltip/TooltipWrapper";
import { cn } from "@/lib/utils/cn";
import { CheckIcon, InfoIcon, PlusIcon, XCircle, XIcon } from "lucide-react";
import {
  type Dispatch,
  forwardRef,
  Fragment,
  type SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  useFormContext,
  type UseFormRegisterReturn,
  useWatch,
} from "react-hook-form";

export interface MultiSelectProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    AsyncSelectProps {
  options: readonly {
    label: string;
    value: string;
    group?: string;
    icon?: React.ComponentType<{ className?: string }>;
    context?: string;
    badges?: string[];
  }[];
  isLoading?: boolean;
  disabledOptions?: { value: string; tooltip?: string }[];
  selectedValues: string[];
  setSelectedValues: Dispatch<SetStateAction<string[]>>;
  onValueChange: (value: string[]) => void;
  showToggleAll?: boolean;
  allSelectedLabel?: string;
  placeholder?: string;
  maxCount?: number;
  modalPopover?: boolean;
  popoverContentClassName?: string;
  popoverSide?: "top" | "bottom";
  commandListMaxHeight?: number;
  className?: string;
  ignoreAllSelected?: boolean;
  showChips?: boolean;
  showFirstSelectionAndCount?: boolean;
  showClearButton?: boolean;
  showClearExtraOptions?: boolean;
  showCloseButton?: boolean;
  showScrollHints?: boolean;
  closeOnSelect?: boolean;
  dataTestId?: string;
  addNewOption?: {
    label: string;
    onClick: () => void;
  };
}

export const MultiSelect = forwardRef<HTMLButtonElement, MultiSelectProps>(
  (
    {
      options,
      disabledOptions,
      isLoading,
      selectedValues,
      setSelectedValues,
      onValueChange,
      allSelectedLabel = "All",
      placeholder = "Select options",
      showToggleAll = true,
      maxCount = 3,
      modalPopover = false,
      popoverContentClassName,
      popoverSide,
      commandListMaxHeight,
      className,
      ignoreAllSelected = false,
      showChips = false,
      showFirstSelectionAndCount = false,
      showClearButton = true,
      showClearExtraOptions = true,
      showCloseButton = true,
      showScrollHints = false,
      closeOnSelect = false,
      dataTestId,
      addNewOption,
      search,
      onSearchChange,
      onOpenChange,
      hasNextPage,
      isFetchingNextPage,
      onLoadMore,
      onRetry,
      isItemsError,
      isItemsLoading,
      matchingValues,
      ...props
    },
    ref,
  ) => {
    // This is needed to allow the select to be disabled when it is inside a disabled fieldset
    const triggerRef = useRef<HTMLButtonElement>(null);
    const popoverContentRef = useRef<HTMLDivElement>(null);
    const [commandListElement, setCommandListElement] =
      useState<HTMLDivElement | null>(null);
    const [scrollEdges, setScrollEdges] = useState({
      top: false,
      bottom: false,
    });
    const isInDisabledFieldset = Boolean(
      triggerRef.current?.closest("fieldset:disabled"),
    );
    const [isPopoverOpen, setIsPopoverOpenState] = useState(false);
    const updateScrollEdges = useCallback(() => {
      const list = commandListElement;
      if (!list) return;
      const hasOverflow = list.scrollHeight > list.clientHeight + 1;
      setScrollEdges({
        top: hasOverflow && list.scrollTop > 1,
        bottom:
          hasOverflow &&
          list.scrollTop + list.clientHeight < list.scrollHeight - 4,
      });
    }, [commandListElement]);
    useEffect(() => {
      if (!showScrollHints || !isPopoverOpen) return;
      const list = commandListElement;
      if (!list) return;
      const frame = requestAnimationFrame(updateScrollEdges);
      const observer = new ResizeObserver(updateScrollEdges);
      observer.observe(list);
      if (list.firstElementChild) observer.observe(list.firstElementChild);
      return () => {
        cancelAnimationFrame(frame);
        observer.disconnect();
      };
    }, [showScrollHints, isPopoverOpen, commandListElement, updateScrollEdges]);
    const setIsPopoverOpen = useCallback(
      (value: boolean) => {
        setIsPopoverOpenState(value);
        onOpenChange?.(value);
      },
      [onOpenChange],
    );
    useEffect(() => {
      if (!modalPopover || !isPopoverOpen) {
        return;
      }

      const handlePointerDown = (event: PointerEvent) => {
        const targetNode = event.target as Node | null;
        if (
          !targetNode ||
          popoverContentRef.current?.contains(targetNode) ||
          triggerRef.current?.contains(targetNode)
        ) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();
        if (typeof event.stopImmediatePropagation === "function") {
          event.stopImmediatePropagation();
        }
        setIsPopoverOpen(false);
      };

      document.addEventListener("pointerdown", handlePointerDown, true);
      return () => {
        document.removeEventListener("pointerdown", handlePointerDown, true);
      };
    }, [modalPopover, isPopoverOpen, setIsPopoverOpen]);

    const handleInputKeyDown = (
      event: React.KeyboardEvent<HTMLInputElement>,
    ) => {
      if (event.key === "Enter") {
        setIsPopoverOpen(true);
      } else if (event.key === "Backspace" && !event.currentTarget.value) {
        const newSelectedValues = [...selectedValues];
        newSelectedValues.pop();
        setSelectedValues(newSelectedValues);
        onValueChange(newSelectedValues);
      }
    };

    const toggleOption = (option: string) => {
      const newSelectedValues = selectedValues.includes(option)
        ? selectedValues.filter((value) => value !== option)
        : [...selectedValues, option];
      setSelectedValues(newSelectedValues);
      onValueChange(newSelectedValues);
      if (closeOnSelect) setIsPopoverOpen(false);
    };

    const handleClear = () => {
      setSelectedValues([]);
      onValueChange([]);
    };

    const handleTogglePopover = () => {
      setIsPopoverOpen(!isPopoverOpen);
    };

    const clearExtraOptions = () => {
      const newSelectedValues = selectedValues.slice(0, maxCount);
      setSelectedValues(newSelectedValues);
      onValueChange(newSelectedValues);
    };

    const toggleAll = () => {
      if (selectedValues.length === options.length) {
        handleClear();
      } else {
        const allValues = options
          .filter(
            (option) =>
              !disabledOptions?.some(
                (disabledOption) => disabledOption.value === option.value,
              ),
          )
          .map((option) => option.value);
        setSelectedValues(allValues);
        onValueChange(allValues);
      }
    };

    const selectedOptions = useMemo(
      () => options.filter((option) => selectedValues.includes(option.value)),
      [selectedValues, options],
    );

    const triggerDisplayText = useMemo(
      () =>
        selectedOptions.length === options.length &&
        !ignoreAllSelected &&
        !onLoadMore
          ? allSelectedLabel
          : selectedOptions
              .slice(0, maxCount)
              .map((item) => item.label)
              .join(", "),
      [
        options,
        maxCount,
        ignoreAllSelected,
        selectedOptions,
        onLoadMore,
        allSelectedLabel,
      ],
    );

    return (
      <Popover
        open={isPopoverOpen}
        onOpenChange={setIsPopoverOpen}
        modal={modalPopover}
      >
        <PopoverTrigger ref={triggerRef} asChild>
          <Button
            ref={ref}
            {...props}
            disabled={props.disabled || isLoading}
            onClick={handleTogglePopover}
            className={cn(
              "bg-card hover:bg-card border-input group flex h-auto min-h-10 w-full items-center justify-between rounded-md border p-1 [&_svg]:pointer-events-auto",
              className,
            )}
            data-testid={dataTestId}
          >
            {isLoading ? (
              <div className="flex w-full items-center justify-center">
                <LoadingSpinner />
              </div>
            ) : (
              <>
                {selectedValues.length > 0 ? (
                  <div className="flex w-full items-center justify-between">
                    <div className="mx-3 flex min-w-0 flex-1 items-center gap-1.5 overflow-hidden">
                      {showFirstSelectionAndCount ? (
                        <span className="text-foreground min-w-0 flex-1 truncate text-[13px] font-medium">
                          {options.find(
                            (option) => option.value === selectedValues[0],
                          )?.label ?? "User"}
                          {selectedValues.length > 1
                            ? ` + ${selectedValues.length - 1}`
                            : null}
                        </span>
                      ) : showChips ? (
                        selectedOptions.length === options.length &&
                        !ignoreAllSelected &&
                        !onLoadMore ? (
                          <Badge className="text-foreground bg-transparent hover:bg-transparent">
                            {allSelectedLabel}
                          </Badge>
                        ) : (
                          <>
                            {selectedOptions
                              .slice(0, maxCount)
                              .map((option) => (
                                <Badge
                                  key={option.value}
                                  className="text-foreground bg-transparent hover:bg-transparent"
                                >
                                  {option.label}
                                  {option.badges?.map((badge) => (
                                    <Badge key={badge} className="ml-2">
                                      {badge}
                                    </Badge>
                                  ))}
                                  {!isInDisabledFieldset && (
                                    <XCircle
                                      className="ml-2 h-4 w-4 cursor-pointer"
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        toggleOption(option.value);
                                      }}
                                    />
                                  )}
                                </Badge>
                              ))}
                            {(selectedValues.length < options.length ||
                              ignoreAllSelected) &&
                              selectedValues.length > maxCount && (
                                <Badge
                                  className={cn(
                                    "border-foreground/1 text-foreground bg-transparent hover:bg-transparent",
                                  )}
                                >
                                  {`+ ${selectedValues.length - maxCount} more`}
                                  {showClearExtraOptions ? (
                                    <XCircle
                                      className={cn(
                                        "ml-2 h-4 w-4 cursor-pointer",
                                        isInDisabledFieldset
                                          ? "cursor-not-allowed"
                                          : "",
                                      )}
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        clearExtraOptions();
                                      }}
                                    />
                                  ) : null}
                                </Badge>
                              )}
                          </>
                        )
                      ) : (
                        <>
                          <span className="text-foreground min-w-0 flex-1 truncate text-[13px] font-medium">
                            {triggerDisplayText}
                          </span>
                          {(selectedValues.length < options.length ||
                            ignoreAllSelected) &&
                            selectedValues.length > maxCount && (
                              <TooltipWrapper
                                message={
                                  <div className="flex flex-col gap-0.5">
                                    {selectedOptions
                                      .slice(maxCount)
                                      .map((o) => (
                                        <span key={o.value}>{o.label}</span>
                                      ))}
                                  </div>
                                }
                              >
                                <Badge
                                  className={cn(
                                    "border-foreground/1 text-foreground shrink-0 bg-transparent hover:bg-transparent",
                                  )}
                                >
                                  {`+ ${selectedValues.length - maxCount} more`}
                                  {showClearExtraOptions ? (
                                    <XCircle
                                      className={cn(
                                        "ml-2 h-4 w-4 cursor-pointer",
                                        isInDisabledFieldset
                                          ? "cursor-not-allowed"
                                          : "",
                                      )}
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        clearExtraOptions();
                                      }}
                                    />
                                  ) : null}
                                </Badge>
                              </TooltipWrapper>
                            )}
                        </>
                      )}
                    </div>
                    <div className="flex items-center justify-between">
                      {!isInDisabledFieldset && showClearButton && (
                        <>
                          <XIcon
                            data-testid={`${dataTestId}-clear-button`}
                            className={cn(
                              "text-muted-foreground mx-2 h-4 cursor-pointer",
                            )}
                            onClick={(event) => {
                              event.stopPropagation();
                              handleClear();
                            }}
                          />
                          <Separator
                            orientation="vertical"
                            className="flex h-full min-h-6"
                          />
                        </>
                      )}
                      <SelectDropdownChevron
                        className={cn(
                          "mx-2",
                          isInDisabledFieldset
                            ? "cursor-default"
                            : "cursor-pointer",
                        )}
                      />
                    </div>
                  </div>
                ) : (
                  <div className="mx-auto flex w-full items-center justify-between">
                    <span className="text-muted-foreground mx-3 text-sm">
                      {placeholder}
                    </span>
                    <SelectDropdownChevron
                      className={cn(
                        "mx-2",
                        isInDisabledFieldset
                          ? "cursor-default"
                          : "cursor-pointer",
                      )}
                    />
                  </div>
                )}
              </>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          ref={popoverContentRef}
          className={cn("w-auto p-0", popoverContentClassName)}
          align="start"
          side={popoverSide}
          data-modal-popover={modalPopover ? "true" : undefined}
        >
          <Command shouldFilter={!onSearchChange}>
            <CommandInput
              placeholder="Search..."
              value={search}
              onValueChange={onSearchChange}
              onKeyDown={handleInputKeyDown}
            />
            <CommandList
              ref={setCommandListElement}
              onScroll={showScrollHints ? updateScrollEdges : undefined}
              style={{
                maxHeight: commandListMaxHeight,
                ...(showScrollHints && (scrollEdges.top || scrollEdges.bottom)
                  ? {
                      maskImage: `linear-gradient(to bottom, ${scrollEdges.top ? "transparent 0, black 40px" : "black 0"}, black calc(100% - ${scrollEdges.bottom ? 48 : 0}px), ${scrollEdges.bottom ? "transparent 100%" : "black 100%"})`,
                    }
                  : {}),
              }}
            >
              <CommandEmpty>
                {isItemsLoading
                  ? "Loading users…"
                  : isItemsError
                    ? "Unable to load users."
                    : "No results found."}
              </CommandEmpty>
              <CommandGroup>
                {showToggleAll && !onLoadMore && (
                  <CommandItem
                    key="all"
                    onSelect={toggleAll}
                    className="cursor-pointer"
                  >
                    <div
                      className={cn(
                        "border-primary mr-2 flex h-4 w-4 items-center justify-center rounded-sm border",
                        selectedValues.length === options.length
                          ? "bg-primary text-primary-foreground"
                          : "opacity-50 [&_svg]:invisible",
                      )}
                    >
                      <CheckIcon className="h-4 w-4" />
                    </div>
                    <span>(Select all)</span>
                  </CommandItem>
                )}
                {options.length === 0 && (
                  <CommandItem className="hover:bg-card">
                    <Typography variant="subHeading">
                      No options found.
                    </Typography>
                  </CommandItem>
                )}
                {options
                  .filter(
                    (option) =>
                      !onSearchChange ||
                      !search?.trim() ||
                      matchingValues?.includes(option.value) ||
                      `${option.label} ${option.context ?? ""} ${option.badges?.join(" ") ?? ""}`
                        .toLowerCase()
                        .includes(search.trim().toLowerCase()),
                  )
                  .map((option, index, visibleOptions) => {
                    const isSelected = selectedValues.includes(option.value);
                    return (
                      <Fragment key={option.value}>
                        {option.group &&
                        (index === 0 ||
                          visibleOptions[index - 1]?.group !== option.group) ? (
                          <div className="text-primary px-2 py-1.5 text-xs font-medium">
                            {option.group}
                          </div>
                        ) : null}
                        <TooltipWrapper
                          message={
                            disabledOptions?.find(
                              (disabledOption) =>
                                disabledOption.value === option.value,
                            )?.tooltip
                          }
                        >
                          <div>
                            <CommandItem
                              onSelect={() => {
                                toggleOption(option.value);
                              }}
                              className="max-w-[80vw] cursor-pointer md:max-w-[500px]"
                              disabled={disabledOptions?.some(
                                (disabledOption) =>
                                  disabledOption.value === option.value,
                              )}
                            >
                              <div
                                className={cn(
                                  "border-primary mr-2 flex h-4 w-4 items-center justify-center rounded-sm border",
                                  isSelected
                                    ? "bg-primary text-primary-foreground"
                                    : "opacity-50 [&_svg]:invisible",
                                )}
                              >
                                <CheckIcon className="h-4 w-4" />
                              </div>
                              <div className="flex flex-col">
                                <div
                                  className={cn(
                                    "flex items-center justify-between",
                                    option.badges?.length &&
                                      "flex-wrap justify-start gap-2",
                                  )}
                                >
                                  {option.icon && (
                                    <option.icon className="text-muted-foreground mr-2 h-4 w-4" />
                                  )}
                                  <span className="line-clamp-2">
                                    {option.label}
                                  </span>
                                  {option.badges?.map((badge) => (
                                    <Badge key={badge}>{badge}</Badge>
                                  ))}
                                  {disabledOptions?.some(
                                    (disabledOption) =>
                                      disabledOption.value === option.value &&
                                      disabledOption.tooltip,
                                  ) && (
                                    <InfoIcon className="text-muted-foreground mr-2 h-4 w-4" />
                                  )}
                                </div>
                                {option.context && (
                                  <span className="text-muted-foreground line-clamp-4 text-xs">
                                    {option.context}
                                  </span>
                                )}
                              </div>
                            </CommandItem>
                          </div>
                        </TooltipWrapper>
                      </Fragment>
                    );
                  })}
              </CommandGroup>
              {onLoadMore && (
                <AsyncSelectOptions
                  hasNextPage={hasNextPage}
                  isFetchingNextPage={isFetchingNextPage}
                  onLoadMore={onLoadMore}
                  onRetry={onRetry}
                  isItemsLoading={isItemsLoading}
                  isItemsError={isItemsError}
                />
              )}
              {(addNewOption ||
                selectedValues.length > 0 ||
                showCloseButton) && <CommandSeparator />}
              {addNewOption && (
                <>
                  <CommandGroup>
                    <CommandItem
                      onSelect={() => {
                        addNewOption.onClick();
                      }}
                    >
                      <div className="flex items-center justify-between">
                        <PlusIcon className="text-muted-foreground mr-2 h-4 w-4" />
                        <span>{addNewOption.label}</span>
                      </div>
                    </CommandItem>
                  </CommandGroup>
                  <CommandSeparator />
                </>
              )}
              {(selectedValues.length > 0 || showCloseButton) && (
                <CommandGroup>
                  <div className="flex items-center justify-between">
                    {selectedValues.length > 0 && (
                      <>
                        <CommandItem
                          onSelect={handleClear}
                          className="flex-1 cursor-pointer justify-center"
                        >
                          Clear
                        </CommandItem>
                        {showCloseButton && (
                          <Separator
                            orientation="vertical"
                            className="flex h-full min-h-6"
                          />
                        )}
                      </>
                    )}
                    {showCloseButton && (
                      <CommandItem
                        onSelect={() => {
                          setIsPopoverOpen(false);
                        }}
                        className="max-w-full flex-1 cursor-pointer justify-center"
                      >
                        Close
                      </CommandItem>
                    )}
                  </div>
                </CommandGroup>
              )}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    );
  },
);
MultiSelect.displayName = "MultiSelect";

interface MultiselectFormFieldProps extends AsyncSelectProps {
  options: MultiSelectProps["options"];
  placeholder?: string;
  register: UseFormRegisterReturn;
  maxCount?: number;
  label?: string;
  disabled?: boolean;
  disabledOptions?: { value: string; tooltip?: string }[];
  dataTestId?: string;
  /** When true, selected values show as removable chips in the trigger. */
  showChips?: boolean;
  showFirstSelectionAndCount?: boolean;
}

export const MultiselectFormField = ({
  options,
  placeholder,
  register,
  maxCount = 3,
  label,
  disabled,
  disabledOptions,
  dataTestId,
  showChips = false,
  showFirstSelectionAndCount = false,
  ...asyncProps
}: MultiselectFormFieldProps) => {
  const form = useFormContext();
  const defaultValue = form.getValues(register.name);
  const watchedValue = useWatch({
    control: form.control,
    name: register.name,
  });
  const [selectedValues, setSelectedValues] = useState<string[]>(
    Array.isArray(defaultValue) ? defaultValue : [],
  );

  useEffect(() => {
    setSelectedValues(Array.isArray(watchedValue) ? watchedValue : []);
  }, [watchedValue]);

  return (
    <FormField
      control={form.control}
      {...register}
      // @ts-expect-error This line is to remove a console error
      ref={null}
      render={({ field }) => (
        <FormItem>
          <div className="flex">
            {label && <FormLabel>{label}</FormLabel>}
            <FormMessage
              data-testid="error-message"
              className="grow text-right"
            />
          </div>
          <FormControl>
            <MultiSelect
              {...asyncProps}
              selectedValues={selectedValues}
              setSelectedValues={setSelectedValues}
              disabled={disabled}
              disabledOptions={disabledOptions}
              options={options}
              onValueChange={field.onChange}
              defaultValue={field.value}
              placeholder={placeholder}
              maxCount={maxCount}
              showChips={showChips}
              showFirstSelectionAndCount={showFirstSelectionAndCount}
              modalPopover
              dataTestId={
                dataTestId ?? `${register.name}-multiselect-form-field`
              }
            />
          </FormControl>
        </FormItem>
      )}
    />
  );
};
